import React, { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ZoomInIcon } from 'lucide-react';
import { Lightbox } from '../Lightbox/Lightbox';
import { PREVIEW_PROJECT, PROJECT_PAGES } from '../../data/welding';
import { useSlideTrack } from '../../hooks/useSlideTrack';
const arrowClass = 'flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-white transition-colors duration-150 ease-out hover:border-arc hover:text-arc-light disabled:opacity-30 disabled:hover:border-white/20 disabled:hover:text-white focus:outline-none focus-visible:ring-4 focus-visible:ring-arc/50';

/**
 * All six real (4:5) pages of Project P01 (Volume 01). One full
 * page per view, never auto-advancing; every page opens in a zoomable lightbox.
 */
export function ProjectPreviewSection() {
  const total = PROJECT_PAGES.length;
  const {
    trackRef,
    active,
    goTo
  } = useSlideTrack(total);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const current = PROJECT_PAGES[active];
  const navigateLightbox = (index: number) => {
    setLightboxIndex(index);
    goTo(index, true);
  };
  const onTrackKeyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      goTo(active + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goTo(active - 1);
    }
  };
  return <section id="project-preview" aria-labelledby="preview-title" className="bg-charcoal py-14 sm:py-20 lg:py-24" data-id="element-237" data-mp="if3ugaucdayk">
      <div className="mx-auto grid w-full max-w-6xl grid-cols-1 px-5 sm:px-8 lg:grid-cols-12 lg:gap-x-14" data-id="element-238" data-mp="deg3fp644u4c">
        <header className="lg:col-span-5 lg:col-start-1 lg:row-start-1 lg:self-end" data-id="element-239" data-mp="9ep6f3pzthrd">
          <p className="label-tech flex items-center gap-3 text-arc-light" data-id="element-240" data-mp="4fkuw6flj7xv">
            <span className="h-px w-6 shrink-0 bg-current" aria-hidden data-id="element-241" data-mp="23rnd7dchhw9y" />
            Real pages · {PREVIEW_PROJECT.volume}
          </p>
          <h2 id="preview-title" className="mt-3 font-heading text-[2.05rem] font-bold uppercase leading-[0.98] text-white min-[390px]:text-[2.25rem] sm:text-[2.8rem] lg:text-[3.1rem]" data-id="element-242" data-mp="1yu5f6i0091gq">
            Explore a complete project, page by page.
          </h2>
          <p className="mt-4 max-w-xl text-[1.1rem] font-semibold leading-snug text-white sm:text-[1.2rem]" data-id="element-243" data-mp="28xtv5ocutyog">
            See all six pages of{' '}
            <span className="text-orange" data-id="element-244" data-mp="2dthli732oxvj">
              Project {PREVIEW_PROJECT.code} — {PREVIEW_PROJECT.name}
            </span>
            , included in {PREVIEW_PROJECT.volume}.
          </p>
          <p className="mt-3 max-w-xl text-[1rem] leading-relaxed text-steel-pale" data-id="element-245" data-mp="35kp4z0yb6tnr">
            The finished build, a 3-view dimensioned plan, the exploded view and cut list, illustrated fabrication
            steps, the weld map, and the Build &amp; Sell card.
          </p>
        </header>

        <div className="mt-8 lg:col-span-7 lg:col-start-6 lg:row-span-2 lg:row-start-1 lg:mt-0" data-id="element-246" data-mp="1ga6h6s63a6as">
          <div className="relative mx-auto w-full max-w-[440px] lg:max-w-[min(64svh,520px)]" data-id="element-247" data-mp="1l53nvsfwc3ki">
            <ul ref={trackRef} tabIndex={0} aria-label={`Project ${PREVIEW_PROJECT.code} pages, ${total} total. Use arrow keys to change page.`} onKeyDown={onTrackKeyDown} className="no-scrollbar flex snap-x snap-mandatory overscroll-x-contain overflow-x-auto rounded-[6px] bg-black shadow-[0_40px_70px_-30px_rgba(0,0,0,0.9)] ring-1 ring-white/10 focus:outline-none focus-visible:ring-4 focus-visible:ring-arc/60" data-id="element-248" data-mp="1dprz6p9m94hg">
              {PROJECT_PAGES.map((page, index) => <li key={page.src} className="w-full shrink-0 snap-center" aria-label={`Page ${index + 1} of ${total}`} data-id="element-249" data-mp="13prb6q0sy06x" data-mpi={index}>
                  <button type="button" onClick={() => setLightboxIndex(index)} tabIndex={index === active ? 0 : -1} aria-label={`Enlarge page ${index + 1}: ${page.caption}`} className="block aspect-[4/5] w-full cursor-zoom-in focus:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-arc" data-id="element-250" data-mp="b8otlpq3lefu">
                    <img src={page.src} alt={page.alt} width={page.width} height={page.height} loading={index === 0 ? 'eager' : 'lazy'} decoding="async" draggable={false} sizes="(min-width: 1024px) 520px, 92vw" className="h-full w-full object-contain" data-id="element-251" data-mp="ttflxuyl9hgt" />
                  </button>
                </li>)}
            </ul>
            <span className="pointer-events-none absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-charcoal/90 px-3 py-1.5 text-[0.82rem] font-semibold text-white ring-1 ring-arc/40" data-id="element-252" data-mp="3ed4pows048i9">
              <ZoomInIcon className="h-4 w-4 text-arc-light" aria-hidden data-id="element-253" data-mp="20hcypyr8g1y8" />
              Tap to enlarge
            </span>
          </div>
        </div>

        <div className="mx-auto mt-5 w-full max-w-[440px] lg:col-span-5 lg:col-start-1 lg:row-start-2 lg:mx-0 lg:mt-10 lg:max-w-none lg:self-start" data-id="element-254" data-mp="1vo0es1xi0jbt">
          <div className="flex items-center justify-between gap-4" data-id="element-255" data-mp="1mq7yovjyd7nd">
            <div className="min-w-0" aria-live="polite" data-id="element-256" data-mp="2jfpbrvl9lvz3">
              <p className="label-tech text-steel-light" data-id="element-257" data-mp="2jf5t2cnsj3k0">
                Page {active + 1} of {total}
              </p>
              <p className="mt-1 font-heading text-[1.3rem] font-semibold uppercase leading-tight text-white" data-id="element-258" data-mp="2fbzpxdvz5shr">
                {current.caption}
              </p>
            </div>
            <div className="flex shrink-0 gap-2" data-id="element-259" data-mp="25feiyn0ea7ba">
              <button type="button" onClick={() => goTo(active - 1)} disabled={active === 0} aria-label="Previous page" className={arrowClass} data-id="element-260" data-mp="yzkqe964kg0a">
                <ChevronLeftIcon className="h-5 w-5" aria-hidden data-id="element-261" data-mp="vj816b2t4lha" />
              </button>
              <button type="button" onClick={() => goTo(active + 1)} disabled={active === total - 1} aria-label="Next page" className={arrowClass} data-id="element-262" data-mp="10fyhmziixj38">
                <ChevronRightIcon className="h-5 w-5" aria-hidden data-id="element-263" data-mp="ri5zzopbxzco" />
              </button>
            </div>
          </div>

          <ol className="mt-4 grid grid-cols-6 gap-2" aria-label="Pages" data-id="element-264" data-mp="hhvoc8iy0zzm">
            {PROJECT_PAGES.map((page, index) => <li key={page.src} data-id="element-265" data-mp="3rs4fo7jdidc" data-mpi={index}>
                <button type="button" onClick={() => goTo(index)} aria-label={`Show page ${index + 1}: ${page.caption}`} aria-current={index === active ? 'true' : undefined} className={`block aspect-[4/5] w-full overflow-hidden rounded-[3px] bg-black transition-[box-shadow,opacity] duration-150 ease-out focus:outline-none focus-visible:ring-4 focus-visible:ring-arc/60 ${index === active ? 'opacity-100 ring-2 ring-arc' : 'opacity-55 ring-1 ring-white/15 hover:opacity-90'}`} data-id="element-266" data-mp="3onu093h9qe8">
                  <img src={page.src} alt="" width={page.width} height={page.height} loading="lazy" decoding="async" className="h-full w-full object-contain" data-id="element-267" data-mp="3gacu5btgfffi" />
                </button>
              </li>)}
          </ol>
        </div>
      </div>

      <Lightbox items={PROJECT_PAGES} index={lightboxIndex} onClose={() => setLightboxIndex(null)} onNavigate={navigateLightbox} label={`Project ${PREVIEW_PROJECT.code} — ${PREVIEW_PROJECT.name}`} counterNoun="Page" data-id="element-268" data-mp="2uwxofrwu9ly" />
    </section>;
}